import MomentUtils from '@date-io/moment';
import {
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@material-ui/core';
import { WithStyles, createStyles, withStyles } from '@material-ui/core/styles';
import { Refresh } from '@material-ui/icons';
import { KeyboardDatePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import _ from 'lodash';
import moment, { Moment } from 'moment';
import * as React from 'react';
import { connect, ConnectedProps } from 'react-redux';
import { IState } from '../../redux/store';
import {
  clampGatewayLogsRangeMs,
  gatewayLogsMaxCalendarDays,
  localCalendarDayEndExclusiveMs,
  localCalendarDayStartMs,
  msToNsString,
} from '../../utils/gatewayLogsRange';
import { getGatewayLogRowSurfaceStyle, IParsedGatewayLogLine, parseGatewayLogLine } from '../../utils/gatewayLogLineParser';
import ReduxLanguage from '../ReduxLanguage';
import translations from '../../translations/i18next';

const styles = createStyles({
  root: {
    margin: 10,
    height: '100%',
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
  },
  toolbarWrap: {
    backgroundColor: '#fafafa',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    borderRadius: 8,
    padding: '16px',
    marginBottom: 12,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    minHeight: 0,
  },
  toolbarRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 14,
    rowGap: 16,
    width: '100%',
  },
  toolbarDateGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 12,
    paddingRight: 0,
    '@media (min-width: 960px)': {
      borderRight: '1px solid rgba(0, 0, 0, 0.1)',
      paddingRight: 18,
      marginRight: 2,
    },
  },
  toolbarFiltersGroup: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 12,
    flex: '1 1 260px',
    minWidth: 0,
  },
  toolbarSearchField: {
    flex: '1 1 200px',
    minWidth: 200,
    maxWidth: 480,
  },
  /** Wide enough for `DD/MM/YYYY` + calendar adornment without clipping. */
  dateField: {
    width: 180,
    minWidth: 180,
    flexShrink: 0,
    marginRight: 0,
    '& .MuiOutlinedInput-input': {
      paddingRight: 6,
    },
  },
  levelFilterField: {
    flex: '0 0 auto',
    minWidth: 200,
    width: 220,
    maxWidth: 260,
  },
  toolbarActions: {
    display: 'flex',
    alignItems: 'center',
    flex: '0 0 auto',
    marginLeft: 'auto',
  },
  rangeHint: {
    fontSize: 11,
    color: '#757575',
    width: '100%',
    marginBottom: 8,
    lineHeight: 1.4,
  },
  tableWrap: {
    flex: 1,
    minHeight: 0,
    width: '100%',
    overflow: 'auto',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    borderRadius: 4,
  },
  table: {
    width: '100%',
    minWidth: 560,
    tableLayout: 'fixed',
    borderCollapse: 'collapse',
  },
  headCell: {
    fontWeight: 700,
    backgroundColor: '#f0f0f0',
    whiteSpace: 'nowrap',
    padding: '6px 8px',
  },
  headCellNarrow: {
    fontWeight: 700,
    backgroundColor: '#f0f0f0',
    padding: '6px 6px',
  },
  cellTime: {
    verticalAlign: 'top',
    fontSize: 11,
    width: 132,
    maxWidth: 132,
    padding: '6px 8px',
    boxSizing: 'border-box',
  },
  cellTimeInner: {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflow: 'visible',
    lineHeight: 1.4,
  },
  cellLevel: {
    verticalAlign: 'top',
    width: 34,
    maxWidth: 34,
    padding: '6px 4px',
    boxSizing: 'border-box',
  },
  cellContext: {
    verticalAlign: 'top',
    fontSize: 11,
    color: '#616161',
    lineHeight: 1.35,
    width: 118,
    maxWidth: 118,
    padding: '6px 8px',
    boxSizing: 'border-box',
  },
  cellContextInner: {
    whiteSpace: 'normal',
    wordBreak: 'break-word',
    overflow: 'visible',
    lineHeight: 1.35,
  },
  cellSummary: {
    verticalAlign: 'top',
    fontSize: 13,
    lineHeight: 1.45,
    width: '36%',
    padding: '6px 8px',
    boxSizing: 'border-box',
  },
  cellRaw: {
    verticalAlign: 'top',
    width: '40%',
    padding: '6px 8px',
    boxSizing: 'border-box',
  },
  scrollCellInner: {
    maxHeight: 140,
    overflow: 'auto',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSize: 10,
    lineHeight: 1.35,
    color: '#555',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  summaryInner: {
    maxHeight: 140,
    overflow: 'auto',
    whiteSpace: 'normal',
    wordBreak: 'break-word',
  },
  summarySegments: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: 6,
    rowGap: 8,
  },
  summarySegment: {
    display: 'inline-flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 4,
    maxWidth: '100%',
    fontSize: 12,
    lineHeight: 1.4,
    padding: '5px 8px',
    borderRadius: 6,
    boxSizing: 'border-box',
    wordBreak: 'break-word',
  },
  summarySegmentNeutral: {
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    border: '1px solid rgba(0, 0, 0, 0.08)',
    color: '#333',
  },
  summarySegmentAction: {
    backgroundColor: 'rgba(90, 192, 177, 0.12)',
    border: '1px solid rgba(90, 192, 177, 0.35)',
    borderLeft: '3px solid #5AC0B1',
    color: '#1b5e4a',
    fontWeight: 500,
  },
  summarySegmentLock: {
    backgroundColor: 'rgba(97, 97, 97, 0.06)',
    border: '1px solid rgba(0, 0, 0, 0.1)',
    color: '#424242',
    fontSize: 11,
  },
  summarySegmentRssi: {
    backgroundColor: 'rgba(25, 118, 210, 0.08)',
    border: '1px solid rgba(25, 118, 210, 0.28)',
    color: '#1565c0',
    fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
    fontSize: 11,
    fontWeight: 500,
  },
  summaryArrow: {
    color: 'rgba(0, 0, 0, 0.45)',
    fontWeight: 600,
    padding: '0 2px',
  },
  loadingBox: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
    flex: 1,
  },
});

export interface IGatewayLogsListOwnProps {
  deviceId: string;
  onFetchLogs: (params: { startNs: string; endNs: string }) => void;
}

/** `'all'` = no level filter; `4` / `6` = only Hypergate error / info lines. */
export type GatewayLogLevelFilterChoice = 'all' | 4 | 6;

export interface IGatewayLogsListState {
  textFilter: string;
  levelFilter: GatewayLogLevelFilterChoice;
  rangeStart: Moment;
  rangeEnd: Moment;
}

type Props = IGatewayLogsListOwnProps & ConnectedProps<typeof connector> & WithStyles<typeof styles>;

function buildContextLine(parsed: IParsedGatewayLogLine): string {
  return _.compact([parsed.project, parsed.functionName, parsed.jobType, parsed.loggerName]).join(' · ');
}

function filterRowsByFullText<T extends { line: string; parsed: IParsedGatewayLogLine; timestampNs: string }>(
  rows: T[],
  textFilter: string,
): T[] {
  const needle = _.trim(textFilter).toLowerCase();
  if (needle === '') {
    return rows;
  }
  return _.filter(rows, (row) => {
    const ctx = buildContextLine(row.parsed);
    const blob = [row.line, row.parsed.summary, ctx, row.parsed.levelLabel || '', row.timestampNs].join('\u0001').toLowerCase();
    return blob.includes(needle);
  });
}

function filterRowsByLogLevel<T extends { parsed: IParsedGatewayLogLine }>(rows: T[], levelFilter: GatewayLogLevelFilterChoice): T[] {
  if (levelFilter === 'all') {
    return rows;
  }
  return _.filter(rows, (row) => row.parsed.levelNumber === levelFilter);
}

function splitSummarySegments(summary: string): string[] {
  const parts = _.filter(summary.split(/\s*·\s*/), (s) => _.trim(s).length > 0);
  return _.isEmpty(parts) ? [summary] : _.map(parts, (s) => _.trim(s));
}

function summarySegmentClassName(segment: string, classes: Record<string, string>): string {
  const s = _.trim(segment);
  if (/^RSSI\s/i.test(s)) {
    return `${classes.summarySegment} ${classes.summarySegmentRssi}`;
  }
  if (/^Lock:/i.test(s)) {
    return `${classes.summarySegment} ${classes.summarySegmentLock}`;
  }
  if (_.includes(s, '→')) {
    return `${classes.summarySegment} ${classes.summarySegmentAction}`;
  }
  return `${classes.summarySegment} ${classes.summarySegmentNeutral}`;
}

function renderSummaryActionSegment(segment: string, classes: Record<string, string>): React.ReactNode {
  const idx = segment.indexOf('→');
  if (idx === -1) {
    return segment;
  }
  const left = _.trim(segment.slice(0, idx));
  const right = _.trim(segment.slice(idx + 1));
  return (
    <>
      <span>{left}</span>
      <span className={classes.summaryArrow}>→</span>
      <span>{right}</span>
    </>
  );
}

function renderFormattedGatewaySummary(summary: string, classes: Record<string, string>): React.ReactNode {
  const segments = splitSummarySegments(summary);
  return (
    <div className={classes.summarySegments}>
      {_.map(segments, (seg, i) => {
        const cls = summarySegmentClassName(seg, classes);
        const isAction = _.includes(seg, '→');
        return (
          <span key={i} className={cls} title={seg}>
            {isAction ? renderSummaryActionSegment(seg, classes) : seg}
          </span>
        );
      })}
    </div>
  );
}

class GatewayLogsList extends React.Component<Props, IGatewayLogsListState> {
  constructor(props: Props) {
    super(props);
    this.state = {
      textFilter: '',
      levelFilter: 'all',
      rangeStart: moment().startOf('day'),
      rangeEnd: moment().endOf('day'),
    };
  }

  public componentDidMount() {
    this.runFetchRespectingClamp();
  }

  /** If clamp shortens the requested window, align pickers so "A" is the real last included day (not a fake 30/03 with end=30/03 00:00). */
  private runFetchRespectingClamp() {
    const requestedEndExclusive = localCalendarDayEndExclusiveMs(this.state.rangeEnd.valueOf());
    const c = this.computeNormalizedClampedRangeMs();
    if (c.endMs < requestedEndExclusive) {
      this.setState(
        {
          rangeStart: moment(c.startMs).startOf('day'),
          rangeEnd: moment(c.endMs).subtract(1, 'millisecond').endOf('day'),
        },
        () => this.props.onFetchLogs(this.buildFetchParamsFromState()),
      );
    } else {
      this.props.onFetchLogs(this.buildFetchParamsFromState());
    }
  }

  /**
   * Loki query_range uses [start, end) with end exclusive. Map calendar "Da–A" (inclusive last day)
   * to start = first day 00:00, end = first instant after the last selected day, then clamp to max window.
   */
  private computeNormalizedClampedRangeMs(): { startMs: number; endMs: number } {
    const startMs = localCalendarDayStartMs(this.state.rangeStart.valueOf());
    const endExclusiveMs = localCalendarDayEndExclusiveMs(this.state.rangeEnd.valueOf());
    return clampGatewayLogsRangeMs(startMs, endExclusiveMs);
  }

  private buildFetchParamsFromState(): { startNs: string; endNs: string } {
    const c = this.computeNormalizedClampedRangeMs();
    return { startNs: msToNsString(c.startMs), endNs: msToNsString(c.endMs) };
  }

  private applyRangeAndFetch = () => {
    const c = this.computeNormalizedClampedRangeMs();
    this.setState(
      {
        rangeStart: moment(c.startMs).startOf('day'),
        rangeEnd: moment(c.endMs).subtract(1, 'millisecond').endOf('day'),
      },
      () => this.props.onFetchLogs(this.buildFetchParamsFromState()),
    );
  };

  private refreshWithCurrentRange = () => {
    this.runFetchRespectingClamp();
  };

  private onRangeStartChange = (next: Moment | null) => {
    if (!next) {
      return;
    }
    const startMs = localCalendarDayStartMs(next.valueOf());
    const maxEndExclusive = moment(startMs).add(gatewayLogsMaxCalendarDays, 'day').valueOf();
    let end = this.state.rangeEnd;
    const requestedEndExclusive = localCalendarDayEndExclusiveMs(end.valueOf());
    if (requestedEndExclusive > maxEndExclusive) {
      end = moment(maxEndExclusive).subtract(1, 'millisecond').endOf('day');
    }
    if (end.isBefore(next)) {
      end = next.clone().endOf('day');
    }
    this.setState({ rangeStart: moment(startMs), rangeEnd: end });
  };

  private onRangeEndChange = (next: Moment | null) => {
    if (!next) {
      return;
    }
    const endExclusiveMs = localCalendarDayEndExclusiveMs(next.valueOf());
    const minStartMs = moment(endExclusiveMs).subtract(gatewayLogsMaxCalendarDays, 'day').valueOf();
    let start = this.state.rangeStart;
    if (localCalendarDayStartMs(start.valueOf()) < minStartMs) {
      start = moment(minStartMs);
    }
    if (next.isBefore(start)) {
      start = next.clone().startOf('day');
    }
    this.setState({ rangeStart: start, rangeEnd: next.endOf('day') });
  };

  public render() {
    const { classes, gatewayLogs, deviceId } = this.props;
    const { textFilter, levelFilter, rangeStart, rangeEnd } = this.state;
    const { entries, loading, errorMessageKey } = gatewayLogs;
    const rows = _.map(entries, (e, index: number) => {
      const parsed = parseGatewayLogLine(e.line);
      return {
        id: `${e.timestampNs}-${index}`,
        timestampNs: e.timestampNs,
        line: e.line,
        parsed,
      };
    });

    const filteredRows = filterRowsByLogLevel(filterRowsByFullText(rows, textFilter), levelFilter);

    return (
      <div className={classes.root}>
        <MuiPickersUtilsProvider utils={MomentUtils} locale={moment.locale()}>
          <div className={classes.rangeHint}>{translations.t('gateways.gatewayLogsRangeHint')}</div>
          <div className={classes.toolbarWrap}>
            <div className={classes.toolbarRow}>
              <div className={classes.toolbarDateGroup}>
                <KeyboardDatePicker
                  className={classes.dateField}
                  disableToolbar
                  variant="inline"
                  inputVariant="outlined"
                  format="DD/MM/YYYY"
                  label={translations.t('gateways.gatewayLogsDateFrom')}
                  value={rangeStart}
                  maxDate={rangeEnd}
                  onChange={this.onRangeStartChange}
                  disabled={loading}
                />
                <KeyboardDatePicker
                  className={classes.dateField}
                  disableToolbar
                  variant="inline"
                  inputVariant="outlined"
                  format="DD/MM/YYYY"
                  label={translations.t('gateways.gatewayLogsDateTo')}
                  value={rangeEnd}
                  minDate={rangeStart}
                  maxDate={moment()}
                  onChange={this.onRangeEndChange}
                  disabled={loading}
                />
                <Button variant="outlined" size="small" onClick={this.applyRangeAndFetch} disabled={loading}>
                  <ReduxLanguage languageKey="gateways.gatewayLogsApplyRange" />
                </Button>
              </div>
              <div className={classes.toolbarFiltersGroup}>
                <FormControl variant="outlined" size="small" className={classes.levelFilterField} disabled={loading || _.isEmpty(rows)}>
                  <InputLabel id="gateway-logs-level-filter-label">{translations.t('gateways.gatewayLogsLevelFilter')}</InputLabel>
                  <Select
                    labelId="gateway-logs-level-filter-label"
                    label={translations.t('gateways.gatewayLogsLevelFilter')}
                    value={levelFilter}
                    onChange={(ev) => {
                      const v = ev.target.value;
                      if (v === 'all') {
                        this.setState({ levelFilter: 'all' });
                      } else if (Number(v) === 4) {
                        this.setState({ levelFilter: 4 });
                      } else {
                        this.setState({ levelFilter: 6 });
                      }
                    }}
                  >
                    <MenuItem value="all">{translations.t('gateways.gatewayLogsLevelFilterAll')}</MenuItem>
                    <MenuItem value={4}>{translations.t('gateways.gatewayLogsLevelError')}</MenuItem>
                    <MenuItem value={6}>{translations.t('gateways.gatewayLogsLevelInfo')}</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  className={classes.toolbarSearchField}
                  size="small"
                  variant="outlined"
                  label={translations.t('gateways.gatewayLogsFilterText')}
                  placeholder={translations.t('gateways.gatewayLogsFilterTextPlaceholder')}
                  value={textFilter}
                  onChange={(ev) => this.setState({ textFilter: ev.target.value })}
                  disabled={loading || _.isEmpty(rows)}
                  fullWidth
                />
              </div>
              <div className={classes.toolbarActions}>
                <Button startIcon={<Refresh />} variant="outlined" size="small" onClick={this.refreshWithCurrentRange} disabled={loading}>
                  <ReduxLanguage languageKey="gateways.gatewayLogsRefresh" />
                </Button>
              </div>
            </div>
          </div>
        </MuiPickersUtilsProvider>
        {errorMessageKey ? (
          <h4 style={{ color: '#c62828' }}>
            <ReduxLanguage languageKey={errorMessageKey} />
          </h4>
        ) : null}
        <div className={classes.tableWrap}>
          {loading ? (
            <div className={classes.loadingBox}>
              <CircularProgress size={32} />
            </div>
          ) : _.isEmpty(rows) ? (
            <div className={classes.loadingBox}>
              <h4 style={{ textAlign: 'center', margin: 0 }}>
                <ReduxLanguage languageKey="gateways.gatewayLogsEmpty" />
              </h4>
            </div>
          ) : _.isEmpty(filteredRows) ? (
            <div className={classes.loadingBox}>
              <h4 style={{ textAlign: 'center', margin: 0 }}>
                <ReduxLanguage languageKey="gateways.gatewayLogsNoFilterMatch" />
              </h4>
            </div>
          ) : (
            <TableContainer style={{ maxHeight: '100%' }}>
              <Table className={classes.table} stickyHeader size="small">
                <colgroup>
                  <col style={{ width: 132 }} />
                  <col style={{ width: 34 }} />
                  <col style={{ width: 118 }} />
                  <col style={{ width: '36%' }} />
                  <col style={{ width: '40%' }} />
                </colgroup>
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.headCellNarrow}>{translations.t('gateways.gatewayLogsTimestamp')}</TableCell>
                    <TableCell className={classes.headCellNarrow} align="center">
                      {translations.t('gateways.gatewayLogsLevel')}
                    </TableCell>
                    <TableCell className={classes.headCell}>{translations.t('gateways.gatewayLogsContext')}</TableCell>
                    <TableCell className={classes.headCell}>{translations.t('gateways.gatewayLogsSummary')}</TableCell>
                    <TableCell className={classes.headCell}>{translations.t('gateways.gatewayLogsFullLine')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {_.map(filteredRows, (row) => {
                    const surface = getGatewayLogRowSurfaceStyle(row.parsed.levelNumber);
                    const contextLine = buildContextLine(row.parsed);
                    const levelIsFour = row.parsed.levelNumber === 4;
                    const levelIsSix = row.parsed.levelNumber === 6;
                    const timeLabel = (() => {
                      const ms = _.toNumber(row.timestampNs) / 1e6;
                      return moment(ms).isValid() ? moment(ms).format('LLL') : row.timestampNs;
                    })();
                    return (
                      <TableRow key={row.id} style={surface}>
                        <TableCell className={classes.cellTime}>
                          <div className={classes.cellTimeInner} title={String(timeLabel)}>
                            {timeLabel}
                          </div>
                        </TableCell>
                        <TableCell className={classes.cellLevel} align="center">
                          <span
                            style={{
                              fontFamily: 'monospace',
                              fontSize: 11,
                              color: levelIsFour ? 'rgba(183, 28, 28, 0.88)' : levelIsSix ? '#212121' : '#424242',
                              fontWeight: levelIsFour ? 600 : 500,
                            }}
                          >
                            {row.parsed.levelLabel || '—'}
                          </span>
                        </TableCell>
                        <TableCell className={classes.cellContext}>
                          <div className={classes.cellContextInner} title={contextLine || undefined}>
                            {contextLine || '—'}
                          </div>
                        </TableCell>
                        <TableCell className={classes.cellSummary}>
                          <div className={classes.summaryInner} title={row.parsed.summary}>
                            {renderFormattedGatewaySummary(row.parsed.summary, classes as Record<string, string>)}
                          </div>
                        </TableCell>
                        <TableCell className={classes.cellRaw}>
                          <div className={classes.scrollCellInner} title={row.line}>
                            {row.line}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>
        <p style={{ fontSize: 11, color: '#888', marginTop: 8 }}>
          {translations.t('gateways.gatewayLogsDeviceIdLabel')}: {deviceId}
        </p>
      </div>
    );
  }
}

function mapStateToProps(state: IState, _own: IGatewayLogsListOwnProps) {
  return {
    gatewayLogs: state.gateways.gatewayLogs,
  };
}

const connector = connect(mapStateToProps);

export default connector(withStyles(styles, { name: 'GatewayLogsList' })(GatewayLogsList));
